import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Typography } from "@mui/material";
import { notification } from "antd";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";

import { getStorage } from "../../../../../../utils/common";
import { JWT_ID_TOKEN, ADMIN_DETAILS } from "../../../../../../dataProvider/constant";
import layoutStyles from "../../EmergencyProceduresLayout.module.css";

const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];
const ALLOWED_MIME_TYPES = [...IMAGE_MIME_TYPES, "application/pdf"];
const ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "pdf"];

const getFileExtension = (name = "") => {
  const parts = `${name}`.split(".");
  return parts.length > 1 ? `${parts[parts.length - 1]}`.toLowerCase() : "";
};

const getOrgFileBasePath = () => {
  try {
    const adminDetails = JSON.parse(getStorage(ADMIN_DETAILS));
    const organisation = adminDetails && adminDetails.organisation ? adminDetails.organisation : null;
    if (!organisation || !organisation.logo_path || !organisation.slug) {
      return "";
    }

    return `${new URL(organisation.logo_path).origin}/${organisation.slug}/`;
  } catch (error) {
    return "";
  }
};

const resolveFileUrl = (value = {}) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  if (value.file_url && /^https?:\/\//i.test(value.file_url)) {
    return value.file_url;
  }

  if (value.url && /^https?:\/\//i.test(value.url)) {
    return value.url;
  }

  const basePath = value.file_base_path || getOrgFileBasePath();

  if (value.file_url) {
    if (/^https?:\/\//i.test(value.file_url)) {
      return value.file_url;
    }

    if (!basePath) {
      return "";
    }

    return `${basePath}${value.file_url}`;
  }

  if (value.file_name) {
    if (/^https?:\/\//i.test(value.file_name)) {
      return value.file_name;
    }

    if (!basePath) {
      return "";
    }

    return `${basePath}${value.file_name}`;
  }

  return "";
};

const getDisplayName = (value = {}) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  if (value.originFileObj && value.originFileObj.name) {
    return value.originFileObj.name;
  }

  if (value.visible_name) {
    return value.visible_name;
  }

  if (value.file_name) {
    const parts = `${value.file_name}`.split("/");
    return parts[parts.length - 1];
  }

  return "";
};

const normalizeFileName = (name = "") => {
  const lastDotIndex = name.lastIndexOf(".");
  const fileExtension = lastDotIndex >= 0 ? name.slice(lastDotIndex + 1) : "";
  const baseName = lastDotIndex >= 0 ? name.slice(0, lastDotIndex) : name;
  const safeName = baseName.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 35) || "file";

  return fileExtension ? `${safeName}.${fileExtension}` : safeName;
};

const isFileLikeObject = (value) => {
  if (!value || typeof value !== "object") {
    return false;
  }

  if (typeof File !== "undefined" && value instanceof File) {
    return true;
  }

  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return true;
  }

  return false;
};

export default function AttachmentMediaUploadBoxField({
  input,
  disabled,
  onPreview,
  s3UploadPath,
  boxWidth = 160,
  boxHeight = 120,
  uploadLabel = "Upload / Attach",
  meta: { touched, error, warning } = {}
}) {
  const fileInputRef = useRef(null);
  const uploadAbortRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");

  const fieldValue = input && input.value && typeof input.value === "object" ? input.value : null;
  const localFileObject = fieldValue && fieldValue.originFileObj;
  const hasValidLocalFile = isFileLikeObject(localFileObject);
  const fileDisplayName = useMemo(() => getDisplayName(fieldValue), [fieldValue]);

  const isPdf = useMemo(() => {
    if (!fieldValue) {
      return false;
    }

    const type = fieldValue.type || fieldValue.file_type || "";
    if (`${type}`.toLowerCase().includes("pdf")) {
      return true;
    }

    return getFileExtension(fileDisplayName || fieldValue.file_name || fieldValue.file_url) === "pdf";
  }, [fieldValue, fileDisplayName]);

  const isImage = useMemo(() => {
    if (!fieldValue) {
      return false;
    }

    const type = fieldValue.type || fieldValue.file_type || "";
    if (IMAGE_MIME_TYPES.includes(type)) {
      return true;
    }

    const extension = getFileExtension(fileDisplayName || fieldValue.file_name || fieldValue.file_url);
    return ["png", "jpg", "jpeg", "webp"].includes(extension);
  }, [fieldValue, fileDisplayName]);

  const remoteFileUrl = useMemo(() => resolveFileUrl(fieldValue || {}), [fieldValue]);
  const hasFile = Boolean(fieldValue && (hasValidLocalFile || fieldValue.file_name || fieldValue.file_url || fieldValue.url));

  useEffect(() => {
    if (hasValidLocalFile) {
      try {
        const previewUrl = URL.createObjectURL(localFileObject);
        setLocalPreviewUrl(previewUrl);

        return () => {
          URL.revokeObjectURL(previewUrl);
        };
      } catch (error) {
        setLocalPreviewUrl("");
        return undefined;
      }
    }

    setLocalPreviewUrl("");
    return undefined;
  }, [hasValidLocalFile, localFileObject]);

  useEffect(() => {
    if (!fieldValue || !hasValidLocalFile || fieldValue.isUploaded || fieldValue.isFailed) {
      return undefined;
    }

    if (!s3UploadPath) {
      notification.error({
        message: "Unable to Process file Upload",
        className: "ant-error"
      });
      if (input && input.onChange) {
        input.onChange(null);
      }
      return undefined;
    }

    const uploadFile = async () => {
      setIsUploading(true);
      uploadAbortRef.current = new AbortController();

      try {
        const region = process.env.REACT_APP_REGION;
        const logins = {
          [process.env.REACT_APP_COGNITO_USER_POOL_ID]: getStorage(JWT_ID_TOKEN)
        };
        const client = new S3Client({
          region,
          credentials: fromCognitoIdentityPool({
            identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
            client: new CognitoIdentityClient({ region }),
            logins,
            clientConfig: { region }
          })
        });

        const organisation = JSON.parse(getStorage(ADMIN_DETAILS)).organisation;
        const selectedFile = localFileObject;
        const cleanedFileName = normalizeFileName(selectedFile.name || "file");
        const relativePath = `${s3UploadPath}/${new Date().getTime()}-${cleanedFileName}`;

        await client.send(
          new PutObjectCommand({
            Bucket: process.env.REACT_APP_TEMP_BUCKET,
            Key: `${organisation.slug}/${relativePath}`,
            Body: selectedFile,
            ContentType: selectedFile.type
          }),
          { abortSignal: uploadAbortRef.current.signal }
        );

        if (input && input.onChange) {
          input.onChange({
            ...fieldValue,
            file_name: relativePath,
            visible_name: selectedFile.name,
            type: selectedFile.type,
            isUploaded: true,
            isFailed: false
          });
        }
      } catch (uploadError) {
        notification.error({
          message: "Failed to upload File.",
          className: "ant-error"
        });

        if (input && input.onChange) {
          input.onChange({
            ...fieldValue,
            isUploaded: true,
            isFailed: true
          });
        }
      } finally {
        setIsUploading(false);
      }
    };

    uploadFile();

    return undefined;
  }, [fieldValue, hasValidLocalFile, input, localFileObject, s3UploadPath]);

  useEffect(() => {
    return () => {
      if (uploadAbortRef.current) {
        uploadAbortRef.current.abort();
      }
    };
  }, []);

  const onSelectFile = (event) => {
    const selectedFile = event && event.target && event.target.files ? event.target.files[0] : null;
    if (!selectedFile) {
      return;
    }

    const extension = getFileExtension(selectedFile.name);
    if (!ALLOWED_MIME_TYPES.includes(selectedFile.type) && !ALLOWED_EXTENSIONS.includes(extension)) {
      notification.error({
        message: "Unsupported File Type",
        description: "Only Images (.jpg, .jpeg, .png, .webp) and PDF are accepted.",
        className: "ant-error"
      });
      event.target.value = "";
      return;
    }

    if (input && input.onChange) {
      input.onChange({
        originFileObj: selectedFile,
        visible_name: selectedFile.name,
        type: selectedFile.type,
        isUploaded: false,
        isFailed: false
      });
    }

    event.target.value = "";
  };

  const onRemoveAttachedFile = () => {
    if (disabled || isUploading) {
      return;
    }

    if (input && input.onChange) {
      input.onChange(null);
    }
  };

  const displayImageUrl = localPreviewUrl || remoteFileUrl;
  const hasUploadFailed = Boolean(fieldValue && fieldValue.isFailed);

  const isUploadPending = Boolean(fieldValue && hasValidLocalFile && !fieldValue.isUploaded);
  const canPreview = Boolean(hasFile && !isUploading && !hasUploadFailed && !isUploadPending && typeof onPreview === "function");
  const canRetryUpload = Boolean(hasUploadFailed && fieldValue && hasValidLocalFile && !disabled && !isUploading);

  const onRetryUpload = () => {
    if (!canRetryUpload || !input || !input.onChange) {
      return;
    }

    input.onChange({
      ...fieldValue,
      isUploaded: false,
      isFailed: false
    });
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.pdf"
        className={layoutStyles.HiddenFileInput}
        onChange={onSelectFile}
      />

      <div
        onClick={() => {
          if (canPreview) {
            onPreview();
          }
        }}
        className={layoutStyles.MediaUploadTile}
        style={{
          width: `${boxWidth}px`,
          height: `${boxHeight}px`,
          border: hasUploadFailed ? "1px solid #D32F2F" : "1px solid #DFD9F1",
          borderRadius: "10px",
          background: hasUploadFailed ? "#d32f2f29" : hasFile ? "#F7F5FC" : "#FCFBFF",
          cursor: canPreview ? "pointer" : "default"
        }}
      >
        {!hasFile ? (
          <button
            type="button"
            disabled={disabled || isUploading}
            onClick={() => {
              if (!disabled && !isUploading && fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            className={layoutStyles.MediaUploadButton}
          >
            <svg height="12px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path fill="currentColor" d="M256 80C256 62.3 241.7 48 224 48C206.3 48 192 62.3 192 80L192 192L80 192C62.3 192 48 206.3 48 224C48 241.7 62.3 256 80 256L192 256L192 368C192 385.7 206.3 400 224 400C241.7 400 256 385.7 256 368L256 256L368 256C385.7 256 400 241.7 400 224C400 206.3 385.7 192 368 192L256 192L256 80z" />
            </svg>
            {uploadLabel}
          </button>
        ) : null}

        {hasFile && isPdf ? (
          <div className={layoutStyles.MediaPdfPreview}>
            <svg width="58" height="58" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
              <path fill="currentColor" d="M128 96C128 60.7 156.7 32 192 32L380.1 32C397 32 413.2 38.7 425.2 50.7L525.3 150.8C537.3 162.8 544 179 544 195.9L544 512C544 547.3 515.3 576 480 576L192 576C156.7 576 128 547.3 128 512L128 96zM384 128L384 208C384 221.3 394.7 232 408 232L488 232L384 128zM224 336C224 322.7 234.7 312 248 312L360 312C373.3 312 384 322.7 384 336C384 349.3 373.3 360 360 360L248 360C234.7 360 224 349.3 224 336zM248 392L392 392C405.3 392 416 402.7 416 416C416 429.3 405.3 440 392 440L248 440C234.7 440 224 429.3 224 416C224 402.7 234.7 392 248 392z" />
            </svg>
            <span className={layoutStyles.MediaPdfLabel}>PDF</span>
          </div>
        ) : null}

        {hasFile && isImage && displayImageUrl ? (
          <img
            src={displayImageUrl}
            alt={fileDisplayName || "Attached file"}
            className={layoutStyles.MediaImagePreview}
          />
        ) : null}

        {isUploading ? (
          <div className={layoutStyles.MediaUploadingOverlay}>
            <CircularProgress size={26} thickness={4} />
            <Typography className={layoutStyles.MediaUploadingText}>Uploading...</Typography>
          </div>
        ) : null}
      </div>

      {hasFile ? (
        <div className={layoutStyles.MediaAttachmentFooter} style={{ width: `${boxWidth}px` }}>
          <div className={layoutStyles.MediaStatusWrap}>
            {hasUploadFailed ? (
              <span className={layoutStyles.MediaStatusText}>
                Failed to Upload, {" "}
                <button
                  type="button"
                  disabled={!canRetryUpload}
                  onClick={onRetryUpload}
                  className={layoutStyles.MediaRetryButton}
                  style={{ cursor: canRetryUpload ? "pointer" : "not-allowed" }}
                  title="Retry upload"
                  aria-label="Retry upload"
                >
                  Retry
                </button>
              </span>
            ) : null}
          </div>

          <div className={layoutStyles.MediaActionsRow}>
            <button
              type="button"
              disabled={disabled || !canPreview}
              onClick={() => {
                if (canPreview) {
                  onPreview();
                }
              }}
              className={layoutStyles.MediaIconButton}
              style={{
                color: canPreview ? "#4B2C79" : "#B8B5C6"
              }}
              title="Preview"
              aria-label="Preview"
            >
              <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M320 96C239.2 96 174.5 132.8 127.4 176.6C80.6 220.1 49.3 272 34.4 307.7C31.1 315.6 31.1 324.4 34.4 332.3C49.3 368 80.6 420 127.4 463.4C174.5 507.1 239.2 544 320 544C400.8 544 465.5 507.2 512.6 463.4C559.4 419.9 590.7 368 605.6 332.3C608.9 324.4 608.9 315.6 605.6 307.7C590.7 272 559.4 220 512.6 176.6C465.5 132.9 400.8 96 320 96zM176 320C176 240.5 240.5 176 320 176C399.5 176 464 240.5 464 320C464 399.5 399.5 464 320 464C240.5 464 176 399.5 176 320zM320 256C320 291.3 291.3 320 256 320C244.5 320 233.7 317 224.3 311.6C223.3 322.5 224.2 333.7 227.2 344.8C240.9 396 293.6 426.4 344.8 412.7C396 399 426.4 346.3 412.7 295.1C400.5 249.4 357.2 220.3 311.6 224.3C316.9 233.6 320 244.4 320 256z"
                />
              </svg>
            </button>

            <button
              type="button"
              disabled={disabled || isUploading}
              onClick={onRemoveAttachedFile}
              className={`${layoutStyles.MediaIconButton} ${layoutStyles.MediaIconButtonDanger}`}
              title="Remove attached photo"
              aria-label="Remove attached photo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M232.7 69.9L224 96L128 96C110.3 96 96 110.3 96 128C96 145.7 110.3 160 128 160L512 160C529.7 160 544 145.7 544 128C544 110.3 529.7 96 512 96L416 96L407.3 69.9C402.9 56.8 390.7 48 376.9 48L263.1 48C249.3 48 237.1 56.8 232.7 69.9zM512 208L128 208L149.1 531.1C150.7 556.4 171.7 576 197 576L443 576C468.3 576 489.3 556.4 490.9 531.1L512 208z" />
              </svg>
            </button>
          </div>
        </div>
      ) : null}

      {touched && ((error && <span className="error-input">{error}</span>) || (warning && <span>{warning}</span>))}
    </div>
  );
}
